import { useMutation, useQuery } from "@apollo/client";
import getSymbolFromCurrency from "currency-symbol-map";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "src/components/Button";
import DialogModal from "src/components/Dialog";
import Icon from "src/components/Icon";
import InnerLoading from "src/components/InnerLoading";
import { AppContext, ValueProps } from "src/context";
import { ATTACH_EVENT_WALLET_TO_SUBACCOUNT } from "src/graphql/mutations";
import { GetEvent, GetProfileDetails } from "src/graphql/queries";
import {
  GetEventWallets,
  GetForeignWalletDetails,
} from "src/graphql/queries/wallets.queries";
import useAppStore from "src/store/utils";
import {
  getForeignAccountDetails_getForeignAccountDetails,
  user,
} from "src/types/api";
import {
  AttachEventWalletToSubaccount,
  AttachEventWalletToSubaccountVariables,
  GetWallets,
  GetWalletsVariables,
} from "src/utils/gql_types";
import { IconType } from "src/utils/icon";
import PaymentHistory from "../PaymentHistory";
import AddForeignAcct from "../modals/AddForiegnAcct";
import ForeignAcctDetails from "../modals/ForeignAcctDetails";
import { useIsDesktop } from "./useDesktop";

interface GetForeignWalletDetailsInterface {
  getForeignAccountDetails: getForeignAccountDetails_getForeignAccountDetails[];
}

enum Currency {
  gbp = "gbp",
  ngn = "ngn",
  usd = "usd",
}

const CashGifts = () => {
  const isPaymentSetup = useContext(AppContext)?.isPaymentSetup;

  const navigate = useNavigate();

  const { event } = useAppStore((state) => state);
  const contextValue = useContext<ValueProps | null>(AppContext);

  const setIsPaymentSetup = useContext(AppContext)?.setIsPaymentSetup;

  const setWalletCode = contextValue?.setWalletCode;

  let [searchParams] = useSearchParams();

  const urlParamAction = searchParams.get("action");

  const { data: eventWalletDetails, loading: eventWalletDetailsLoading } =
    useQuery<GetWallets, GetWalletsVariables>(GetEventWallets, {
      skip: !event?.uuid,
      variables: {
        event_id: event.uuid,
      },
      // fetchPolicy: "network-only",
      fetchPolicy: "cache-first",
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        } else {
          navigate("/clear");
        }
      },
    });

  const { data: foreignWalletDetails, loading: foreignDetailsLoading } =
    useQuery<GetForeignWalletDetailsInterface>(GetForeignWalletDetails, {
      skip: !event.id,
      variables: {
        eventId: event.id,
      },
      fetchPolicy: "cache-first",
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        } else {
          navigate("/clear");
        }
      },
    });

  const [addForeignAcct, setAddForeignAcct] = useState(false);

  const [viewForeignAcct, setViewForeignAcct] = useState(false);

  const colors = useMemo(() => {
    return {
      ngn: "bg-[#34544F]",
      usd: "bg-gradient-to-r from-green-600 to-lime-700",
      gbp: "bg-gradient-to-r from-teal-600 to-emerald-700",
      // ghs: "bg-gradient-to-r from-teal-600 to-emerald-700",
      // zar: "bg-gradient-to-r from-teal-600 to-emerald-700",
    };
  }, []);

  // "bg-gradient-to-r from-teal-600 to-emerald-700",
  // "bg-gradient-to-r from-red-600 to-orange-700",
  // "bg-gradient-to-r from-purple-600 to-pink-700",
  // "bg-gradient-to-r from-amber-600 to-yellow-700",
  // "bg-gradient-to-r from-rose-600 to-fuchsia-700",

  useEffect(() => {
    if (urlParamAction === "add_dollar_details") {
      setAddForeignAcct(true);
    }
  }, [urlParamAction]);

  const nairaWallet = eventWalletDetails?.getWallets?.find(
    (item) => !!item && item?.currency === Currency.ngn
  );

  const walletsArray = useMemo(
    () =>
      eventWalletDetails?.getWallets
        ? eventWalletDetails.getWallets
            // .filter((item) => item?.status === "verified")
            .filter((item) => item?.currency === "ngn")
            .map((item) => {
              if (item)
                return {
                  status: item?.status,
                  currency: item?.currency,
                  color: colors[item?.currency as keyof typeof colors],
                  balance: new Intl.NumberFormat(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.gift_volume),
                };
              return null;
            })
            .filter(Boolean)
        : [],
    [eventWalletDetails, colors]
  );

  const foreignCardsArray = useMemo(() => {
    // Return empty array if foreignWalletDetails or getForeignAccountDetails is undefined
    if (!foreignWalletDetails?.getForeignAccountDetails) {
      return [];
    }

    return foreignWalletDetails?.getForeignAccountDetails
      .filter((wallet) => wallet != null) // Type guard to remove null/undefined
      .map((item) => ({
        currency: item?.currency,
        color: colors[item?.currency as keyof typeof colors],
      }));
  }, [foreignWalletDetails, colors]);

  const [cardsArray, setCardsArray] = useState([
    ...walletsArray,
    ...foreignCardsArray,
  ]);

  const currentCard = cardsArray && cardsArray[0];

  useEffect(() => {
    setCardsArray([...walletsArray, ...foreignCardsArray]);
  }, [walletsArray, foreignCardsArray]);

  const swapCards = (currency: string) => {
    setCardsArray((prevArray) => {
      // Find the index of the card with the matching currency
      const index = prevArray.findIndex((card) => card?.currency === currency);

      // If the currency is found, rearrange the array
      if (index !== -1) {
        const newArray = [...prevArray];
        const [selectedCard] = newArray.splice(index, 1); // Remove the selected card
        return [selectedCard, ...newArray]; // Place it at the start
      }

      // If currency not found, return the original array
      return prevArray;
    });
  };

  const currencyArray =
    foreignWalletDetails?.getForeignAccountDetails
      ?.filter((wallet) => wallet !== null && wallet !== undefined)
      .map((wallet) => wallet?.currency) ?? [];

  useEffect(() => {
    if (nairaWallet?.id) {
      localStorage.setItem("walletCode", JSON.stringify(nairaWallet.id));
      setWalletCode && setWalletCode(nairaWallet.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nairaWallet?.id]);

  const foreignDetail = useMemo(() => {
    const res = foreignWalletDetails?.getForeignAccountDetails?.find(
      (item) => item?.currency === currentCard?.currency
    );
    return res;
  }, [foreignWalletDetails, currentCard]);

  const { data: profile } = useQuery<user>(GetProfileDetails);

  const [isAttachAccountOpen, setIsAttachAccountOpen] = useState(false);

  const handleSetup = () => {
    const kycStatus = profile?.user?.kyc_record?.status;
    if (!isPaymentSetup && kycStatus === "completed") {
      // open the modal for attaching bank details here
      setIsAttachAccountOpen(true);
    } else {
      navigate("/payment-setup", {
        state: { from: "cash-gifts" },
      });
    }
  };

  const [attachEventWalletToSubaccount, { loading }] = useMutation<
    AttachEventWalletToSubaccount,
    AttachEventWalletToSubaccountVariables
  >(ATTACH_EVENT_WALLET_TO_SUBACCOUNT);

  const handleAddAccount = () => {
    attachEventWalletToSubaccount({
      variables: {
        event_id: event.uuid,
        currency: "ngn",
      },
      onCompleted() {
        toast.success("Bank details successfully attached");
        setIsPaymentSetup && setIsPaymentSetup(true);
        setIsAttachAccountOpen(false);
      },
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">
              {error?.message ??
                "An error occured uploading your bank details. Please try again"}
            </p>
          );
        } else {
          navigate("/clear");
        }
      },
      refetchQueries: [
        { query: GetEvent, variables: { eventId: event?.id } },
        {
          query: GetEventWallets,
          variables: {
            event_id: event.uuid,
          },
        },
      ],
    });
  };

  const infoRef = useRef<HTMLDivElement>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // Handle click outside to close info box
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(event.target as Node)) {
        setInfoOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isDesktop = useIsDesktop();

  return (
    <>
      {eventWalletDetailsLoading && (
        <div className="flex w-full items-center justify-center">
          <InnerLoading />
        </div>
      )}

      {!foreignDetailsLoading && foreignDetail && (
        <DialogModal open={viewForeignAcct} onOpenChange={setViewForeignAcct}>
          <ForeignAcctDetails
            foreignDetails={foreignDetail}
            setOpen={setViewForeignAcct}
          />
        </DialogModal>
      )}

      <DialogModal open={addForeignAcct} onOpenChange={setAddForeignAcct}>
        <AddForeignAcct
          currencyArray={currencyArray}
          setOpen={setAddForeignAcct}
        />
      </DialogModal>

      <DialogModal
        open={isAttachAccountOpen}
        onOpenChange={setIsAttachAccountOpen}
      >
        <div className="rounded bg-white p-8">
          <h1 className="mb-2 text-2xl font-bold">Add your Naira account</h1>
          <p className="mb-4 text-gray-500">Add your saved Naira acount</p>

          <div className="mb-12 flex flex-col gap-8">
            <div className="flex w-full flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-4 rounded border p-2">
                {/* <div className="h-14 w-14 flex-shrink-0 rounded-full bg-orange-500"></div> */}
                <p className="uppercase text-gray-500">
                  {profile?.user?.kyc_record?.bank_details?.[0]?.bank_name}
                </p>
              </div>

              {/* <div className="flex gap-4"> */}
              <div className="rounded border p-2">
                <p className="uppercase text-gray-500">
                  {profile?.user?.kyc_record?.bank_details?.[0]?.account_name}
                </p>
              </div>

              <div className="w-fit rounded border p-2">
                <p className="uppercase text-gray-500">
                  {profile?.user?.kyc_record?.bank_details?.[0]?.account_number}
                </p>
              </div>
            </div>
            {/* </div> */}

            <div className="flex h-[100px] w-full items-center justify-center rounded border border-[#B95900] p-4">
              <p className="text-sm text-gray-500 sm:text-base">
                This is where your money would be sent to automatically by our
                payment partners
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <div className="flex w-full gap-2 sm:w-3/5 sm:gap-4">
              <Button
                design="secondary"
                label="Cancel"
                className="text-sm sm:text-base"
                onClick={() => setIsAttachAccountOpen(false)}
              >
                Cancel
              </Button>
              <Button
                loader={loading}
                label="Add Account"
                className="text-sm sm:text-base"
                onClick={handleAddAccount}
              >
                Add Account
              </Button>
            </div>
          </div>
        </div>
      </DialogModal>

      <div className="flex flex-col items-center justify-center gap-12 p-4">
        <div className="">
          {/* <h5 className="mb-4 text-center text-sm tracking-widest text-gray-500 md:text-base">
            CURRENCY
          </h5> */}
          <div className="flex w-full flex-col items-center justify-center gap-8 md:w-[500px]">
            <div className="flex w-[320px] flex-wrap items-center justify-center gap-2 text-[#D4D4D8]">
              {["ngn", ...currencyArray].map((currency, index) => (
                <button
                  key={index}
                  className={`cursor-pointer transition duration-300 ${currentCard?.currency === currency ? `text-[#008636]` : `hover:text-gray-400`}`}
                  onClick={() => {
                    swapCards(currency);
                  }}
                >
                  <Icon iconName={currency as IconType} />
                </button>
              ))}
              <div
                className="cursor-pointer transition duration-300 hover:text-gray-400"
                onClick={() => setAddForeignAcct(true)}
              >
                <Icon iconName="currencyPlus" />
              </div>
            </div>

            {(currentCard?.currency === "ngn" &&
              "status" in currentCard &&
              currentCard?.status === "verified") ||
            currentCard?.currency !== "ngn" ? (
              <div>
                <div
                  className="relative mb-8 flex flex-col items-center"
                  style={{
                    height: `${(isDesktop ? 180 : 120) + 20 * (cardsArray.length - 1)}px`,
                    // Dynamic height based on number of cards (120px height + 20px offset per card)
                  }}
                >
                  {cardsArray.map((card, index) => (
                    <div
                      key={index}
                      className={`absolute flex h-[120px] flex-col items-center justify-center gap-2 rounded-3xl shadow-lg md:h-[180px] ${card?.color}`}
                      style={{
                        top: `${index * 20}px`, // Adjusts the vertical offset for each card
                        // backgroundColor: card?.color,
                        zIndex: 10 - index, // Ensures the first card is on top
                        // width: `${300 - index * 20}px`,
                        width: `${(isDesktop ? 450 : 300) - index * 20}px`,
                      }}
                    >
                      {currentCard?.currency === "ngn" ? (
                        <div
                          className="group relative flex cursor-pointer items-center gap-2 bg-gradient-to-r from-white to-white bg-[length:0_1px] bg-left-bottom bg-no-repeat transition-[background-size] duration-300 hover:bg-[length:100%_1px]"
                          onClick={() => {
                            setInfoOpen(!infoOpen);
                          }}
                        >
                          <p className="text-xs font-bold text-white">
                            Total payouts in Naira
                            {/* <span className="uppercase">{card?.currency}</span> */}
                          </p>
                          <span className="mb-1 text-white">
                            <Icon iconName="info" />
                          </span>

                          {card?.currency === "ngn" && (
                            <div
                              ref={infoRef}
                              className="absolute -left-[50px] bottom-[20px] hidden w-[250px] rounded-lg bg-[#CFD9D8D4] p-2 group-hover:block lg:-bottom-[100px] lg:left-[150px]"
                            >
                              <h5 className="mb-2 text-xs font-bold text-[#34544F]">
                                Payouts, not balance
                              </h5>
                              <p className="text-xs font-semibold text-[#3B5F59]">
                                These numbers represent the total amount that
                                has been paid out to your beneficiary account,
                                not your current balance, as we do not hold your
                                funds
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs font-bold uppercase text-white">
                          {card?.currency}
                        </span>
                      )}
                      {card && card === currentCard && "balance" in card && (
                        <p className="text-3xl font-medium text-white md:text-4xl">{`${getSymbolFromCurrency(card?.currency ?? "")} ${card?.balance?.toLocaleString() ?? "0"}`}</p>
                      )}
                      {/* <p className="mt-4 text-xs font-bold text-white">
                        Payouts are made within 24 hours or less
                      </p> */}
                      {currentCard?.currency !== "ngn" && foreignDetail && (
                        <button
                          className="text-xs text-white transition-all duration-500 hover:underline"
                          onClick={() => setViewForeignAcct(true)}
                        >
                          See account details
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mb-4 w-full rounded-lg bg-[#CFD9D8D9] p-2 text-center sm:w-[460px] md:w-full lg:w-[460px]">
                  <p className="text-xs font-semibold text-[#3B5F59]">
                    All cash gifts are sent by our payment processors to your
                    beneficiary account within 24 business hours (excluding
                    weekends and public holidays)
                  </p>
                </div>

                <div className="flex flex-col items-center justify-center gap-2">
                  <p className="text-xs text-gray-400">powered with</p>
                  <div className="flex items-center gap-4">
                    <Icon iconName="paystackWord" />
                    <span>|</span>
                    <Icon iconName="stripe" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-8">
                <div className="flex w-full flex-col items-center justify-center rounded-[34.83px] bg-[#34544F] px-12 py-4 sm:max-w-[450px]">
                  <div className="flex flex-col items-center justify-center sm:w-[70%]">
                    <p className="text-center text-xs text-white">
                      You can’t recieve cash gifts in naira if you don’t setup
                      your cash gifts here
                    </p>

                    <div className="sm:h-[100px] sm:w-[300px]">
                      <img
                        src="./backdrop.png"
                        alt=""
                        className="h-full w-full bg-cover"
                      />
                    </div>

                    <button
                      className="rounded-md border border-white px-5 py-2 text-sm text-white transition duration-500 hover:ring-1 hover:ring-white"
                      onClick={handleSetup}
                    >
                      Setup Naira
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* {currentCard && currentCard?.currency !== "ngn" ? (
              
            ) : (
              
            )} */}
          </div>
        </div>

        <div className="w-full sm:w-[500px]">
          <h5 className="mb-2 text-center text-sm tracking-widest text-gray-500 md:text-base">
            HISTORY
          </h5>
          <PaymentHistory />
        </div>
      </div>
    </>
  );
};

export default CashGifts;
